import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Funnel, DownloadSimple } from "@phosphor-icons/react";
import { api } from "../lib/api";
import { timeAgo, severityColor, regionsList } from "../lib/format";

const CATS = ["all", "irritation", "allergy", "counterfeit", "packaging", "performance", "fragrance_issue"];

export default function ComplaintsPage() {
  const [all, setAll] = useState([]);
  const [cat, setCat] = useState("all");
  const [region, setRegion] = useState("all");

  useEffect(() => {
    (async () => {
      const rows = await api.latestComplaints(200);
      setAll(rows);
    })();
  }, []);

  const list = useMemo(() => {
    return all.filter((c) => (cat === "all" || c.category === cat) && (region === "all" || c.region === region));
  }, [all, cat, region]);

  return (
    <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-10 py-8 lg:py-12" data-testid="complaints-page">
      <div className="eyebrow">Global feed</div>
      <h1 className="font-heading font-light text-4xl sm:text-5xl tracking-tight mt-2">
        All incoming <span className="font-accent italic text-[#A66E4E]">complaints</span>
      </h1>
      <p className="text-[#6E6B65] mt-3 max-w-xl">
        Every signal collected across social, retail, and news, across all monitored regions.
      </p>

      <div className="mt-8 flex items-center justify-between gap-3">
        <div />
        <a
          data-testid="export-csv-btn"
          href={api.exportComplaintsCsvUrl({
            limit: 500,
            ...(cat !== "all" ? { category: cat } : {}),
            ...(region !== "all" ? { region } : {}),
          })}
          className="inline-flex items-center gap-2 px-4 py-2 border border-[#1C1B1A] text-[11px] uppercase tracking-[0.18em] font-semibold text-[#1C1B1A] hover:bg-[#1C1B1A] hover:text-white transition-colors"
        >
          <DownloadSimple size={13} weight="bold" />
          Export CSV
        </a>
      </div>

      <div className="mt-4 border border-[#E5E2DC] bg-white">
        <div className="border-b border-[#E5E2DC] p-4 flex flex-wrap gap-3 items-center">
          <div className="eyebrow flex items-center gap-1"><Funnel size={11} /> Filters</div>
          <div className="flex flex-wrap gap-1" data-testid="cat-filter">
            {CATS.map((c) => (
              <button
                key={c}
                type="button"
                data-testid={`cfilter-${c}`}
                onClick={() => setCat(c)}
                className={`px-2.5 py-1 text-[11px] tracking-[0.14em] uppercase font-semibold border transition-colors ${
                  cat === c
                    ? "bg-[#1C1B1A] text-white border-[#1C1B1A]"
                    : "bg-white text-[#1C1B1A] border-[#E5E2DC] hover:bg-[#EFECE6]"
                }`}
              >
                {c.replace("_", " ")}
              </button>
            ))}
          </div>
          <div className="flex flex-wrap gap-1" data-testid="region-filter">
            <button
              type="button"
              onClick={() => setRegion("all")}
              className={`px-2.5 py-1 text-[11px] tracking-[0.14em] uppercase font-semibold border transition-colors ${
                region === "all"
                  ? "bg-[#A66E4E] text-white border-[#A66E4E]"
                  : "bg-white text-[#1C1B1A] border-[#E5E2DC] hover:bg-[#EFECE6]"
              }`}
            >
              all regions
            </button>
            {regionsList.map((r) => (
              <button
                key={r}
                type="button"
                data-testid={`rfilter-${r}`}
                onClick={() => setRegion(r)}
                className={`px-2.5 py-1 text-[11px] tracking-[0.14em] uppercase font-semibold border transition-colors ${
                  region === r
                    ? "bg-[#A66E4E] text-white border-[#A66E4E]"
                    : "bg-white text-[#1C1B1A] border-[#E5E2DC] hover:bg-[#EFECE6]"
                }`}
              >
                {r}
              </button>
            ))}
          </div>
          <span className="ml-auto eyebrow">{list.length} signals</span>
        </div>

        <div className="divide-y divide-[#E5E2DC]">
          {list.map((c) => {
            const sev = severityColor(c.severity);
            return (
              <Link
                to={`/product/${c.product_id}`}
                key={c.id}
                className="flex gap-4 p-5 hover:bg-[#EFECE6]/40"
                data-testid="complaint-row"
              >
                <div className="w-20 shrink-0">
                  <div className="eyebrow text-[9px]">{c.source}</div>
                  <div className="text-[10px] font-mono text-[#6E6B65] mt-1">{timeAgo(c.created_at)}</div>
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    <span
                      className="text-[10px] uppercase tracking-[0.18em] font-semibold px-1.5 py-0.5"
                      style={{ background: sev.bg, color: sev.fg }}
                    >
                      {c.severity}
                    </span>
                    <span className="eyebrow text-[9px]">{c.category.replace("_", " ")}</span>
                    <span className="eyebrow text-[9px]">· {c.region}</span>
                  </div>
                  <div className="font-heading text-[15px] mb-1">
                    {c.brand} — <span className="italic">{c.product_name}</span>
                  </div>
                  <p className="text-[13px] text-[#1C1B1A]/85 leading-snug">{c.text}</p>
                </div>
              </Link>
            );
          })}
          {list.length === 0 && (
            <div className="p-10 text-center text-[#6E6B65]">No matching signals.</div>
          )}
        </div>
      </div>
    </div>
  );
}
